import { FormEvent, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearAuthError, loginRequest } from '../../features/auth/authSlice';

const CONSULT_URL = import.meta.env.VITE_CONSULT_URL || 'http://localhost:1706';

const FIELD: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1.5px solid #f9c0d8',
  fontSize: 14,
  background: '#fff5f8',
  color: '#6b0030',
  outline: 'none',
  boxSizing: 'border-box',
};

const BTN: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '11px 0',
  borderRadius: 12,
  fontWeight: 700,
  fontSize: 14,
  background: '#ffffff',
  color: '#d6006e',
  border: '2px solid #d6006e',
  boxShadow: '0 4px 16px rgba(212,0,106,0.14)',
  cursor: 'pointer',
};

function StarField() {
  const stars = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        id: i,
        left: `${(i * 37) % 92}%`,
        top: `${(i * 53) % 88}%`,
        size: 2 + (i % 3),
        delay: `${(i % 7) * 0.35}s`,
      })),
    [],
  );

  return (
    <>
      {stars.map((s) => (
        <span
          key={s.id}
          className="pointer-events-none absolute rounded-full bg-white"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            opacity: 0.35,
            animation: `mz-star 3.2s ease-in-out ${s.delay} infinite`,
          }}
        />
      ))}
    </>
  );
}

function DiagonalShards() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {[
        { t: '8%', l: '12%', w: 220, rot: -28, o: 0.08 },
        { t: '22%', l: '48%', w: 280, rot: -32, o: 0.06 },
        { t: '55%', l: '5%', w: 320, rot: -30, o: 0.07 },
        { t: '70%', l: '40%', w: 240, rot: -34, o: 0.05 },
      ].map((s, i) => (
        <div
          key={i}
          className="absolute h-[2px] bg-white"
          style={{
            top: s.t,
            left: s.l,
            width: s.w,
            opacity: s.o,
            transform: `rotate(${s.rot}deg)`,
            boxShadow: '0 0 24px rgba(255,255,255,0.25)',
          }}
        />
      ))}
    </div>
  );
}

function WaveEdge() {
  return (
    <svg
      className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[42%] md:block lg:w-[38%]"
      viewBox="0 0 420 900"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="waveFill" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.92" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
        </linearGradient>
      </defs>
      <path
        d="M140 0 C90 90, 210 160, 150 260 C80 380, 220 450, 160 560 C95 680, 200 760, 130 900 L420 900 L420 0 Z"
        fill="url(#waveFill)"
      />
    </svg>
  );
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, token } = useAppSelector((s) => s.auth);
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const fromWeb = from === 'web';
  const fromWelcome = from === 'welcome';
  const expired = searchParams.get('expired') === '1';
  const [email, setEmail] = useState(() => searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (token) navigate('/', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    dispatch(loginRequest({ email: email.trim(), password }));
  };

  return (
    <div className="relative flex h-screen max-h-screen flex-col overflow-hidden bg-[#2b0018]">
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(232,0,122,0.55), transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(255,79,162,0.35), transparent 45%), linear-gradient(145deg, #1a000f 0%, #7a0038 35%, #d6006e 70%, #ff4fa2 100%)',
        }}
      />
      <StarField />
      <DiagonalShards />
      <div
        className="mz-orb pointer-events-none absolute left-[18%] top-[28%] h-64 w-64 rounded-full blur-3xl"
        style={{ background: 'rgba(255,120,180,0.45)' }}
      />
      <WaveEdge />

      <div className="relative z-20 flex shrink-0 items-center gap-3 px-6 pt-5 sm:px-10 lg:px-14 mz-fade-up">
        <BrandLogo size={34} className="brightness-0 invert" />
        <div>
          <p className="text-lg font-black tracking-tight text-white">Micus</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/70">Workspace</p>
        </div>
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 items-center gap-6 overflow-hidden px-6 py-4 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:px-14 lg:py-2">
        <div className="mz-fade-up mz-d1 hidden max-w-xl text-white lg:block">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-white/70">
            Secure sign-in
          </p>
          <h1 className="text-4xl font-black leading-[1.1] tracking-tight xl:text-5xl">
            Sign in to your workspace
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">
            Orchestrate company intake, country-aware documents, AI verification, and
            remediation — one enterprise platform.
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {['Encrypted', 'Document vault', 'AI verify', 'Remediate'].map((pill) => (
              <span
                key={pill}
                className="rounded-full px-3 py-1 text-xs font-semibold text-[#1a0010]"
                style={{
                  background: 'rgba(255,255,255,0.72)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {pill}
              </span>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            {['#d6006e', '#3b82f6', '#f59e0b', '#10b981'].map((c, i) => (
              <div
                key={c}
                className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white"
                style={{ background: c, marginLeft: i ? -8 : 0 }}
              >
                {['A', 'C', 'R', 'P'][i]}
              </div>
            ))}
            <span className="ml-1 text-sm font-medium text-white/80">
              Companies & brokers manage every rating here
            </span>
          </div>
        </div>

        <div className="relative z-20 mx-auto w-full max-w-[380px] mz-fade-up mz-d2">
          <div
            className="rounded-2xl p-6 sm:p-7"
            style={{
              background: 'rgba(255,255,255,0.94)',
              boxShadow: '0 24px 60px rgba(212,0,106,0.22), 0 8px 20px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.8)',
            }}
          >
            <div className="mb-4">
              <div className="mb-1 flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#fdf2f8] text-[10px] font-bold text-[#d6006e]">
                  ✓
                </span>
                <span className="text-xs font-bold uppercase tracking-widest text-[#d6006e]">
                  Workspace access
                </span>
              </div>
              <h2 className="text-xl font-black tracking-tight text-[#6b0030]">Welcome back</h2>
              <p className="mt-0.5 text-sm text-[#c084a0]">
                Continue registration, uploads, and remediation.
              </p>
            </div>

            {fromWeb && !error && (
              <div
                role="status"
                className="mb-3 rounded-xl px-3.5 py-2.5 text-sm"
                style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', color: '#9d0050' }}
              >
                <strong className="block text-[13px]">You already have a Micus account</strong>
                <span className="text-xs text-[#be185d]">
                  Sign in to continue your rating &amp; raise journey. Just applied? Use the temporary app password from your
                  welcome email.
                </span>
              </div>
            )}

            {expired && !error && (
              <div
                role="status"
                className="mb-3 rounded-xl px-3.5 py-2.5 text-sm"
                style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', color: '#9d0050' }}
              >
                Your session expired. Sign in again to continue.
              </div>
            )}

            {fromWelcome && !error && (
              <div
                role="status"
                className="mb-3 rounded-xl px-3.5 py-2.5 text-sm"
                style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', color: '#9d0050' }}
              >
                <strong className="block text-[13px]">Welcome to Micus</strong>
                <span className="text-xs text-[#be185d]">
                  Sign in with the temporary app password from your welcome email. You&apos;ll set your own password next.
                </span>
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="mb-3 rounded-xl px-3.5 py-2.5 text-sm"
                style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c' }}
              >
                {error}
                {/Consult/.test(error) && (
                  <a href={CONSULT_URL} className="mt-1 block text-xs font-bold underline">
                    Open Micus Consult →
                  </a>
                )}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-3.5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#9d0050]">Email</label>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mz-field"
                  style={FIELD}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#9d0050]">Password</label>
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-xs font-semibold text-[#d6006e]"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoFocus={(fromWeb || fromWelcome) && Boolean(email)}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mz-field"
                  style={FIELD}
                />
              </div>

              <button type="submit" disabled={loading} className="mz-cta" style={BTN}>
                {loading ? 'Signing in…' : 'Continue →'}
              </button>
            </form>

            <div
              className="mt-4 flex items-center justify-between gap-2 rounded-xl p-2.5"
              style={{ background: '#fdf2f8', border: '1px solid #fce7f3' }}
            >
              <div>
                <p className="text-xs font-semibold text-[#be185d]">Customer workspace</p>
                <p className="mt-0.5 text-[11px] text-[#c084a0]">Micus analyst? Use Micus Consult.</p>
              </div>
              <a href={CONSULT_URL} className="shrink-0 text-xs font-bold text-[#d6006e] hover:underline">
                Consult →
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-20 flex shrink-0 flex-wrap justify-center gap-2 px-6 pb-4 pt-1 mz-fade-up mz-d3">
        {[
          { label: 'Intake', hint: 'Register & upload' },
          { label: 'Verify', hint: 'AI document checks' },
          { label: 'Remediate', hint: 'Re-analyze together' },
        ].map((item) => (
          <div
            key={item.label}
            className="rounded-full px-3.5 py-1.5 text-center"
            style={{
              background: 'rgba(255,255,255,0.55)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.35)',
            }}
          >
            <span className="text-xs font-bold text-[#1a0010]">{item.label}</span>
            <span className="ml-1.5 text-[10px] font-medium text-[#6b0030]/70">{item.hint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
